<template>
  <v-container class="px-xl-7 bg-gainsboro surgeon-journal-expanded" fluid @click.stop>
    <div v-if="getLoading" class="d-flex justify-center align-center py-8">
      <div class="white rounded-xl pa-4">Indlæser journal...</div>
    </div>
    <div v-else-if="visitData" class="surgeon-journal-expanded__content">
      <h2 class="text-subtitle-1 text-uppercase mb-4">JOURNAL-KLADDE</h2>

      <v-row class="my-0 align-end surgeon-journal-expanded__meta-fields">
        <v-col cols="12" sm="4" md="3" class="py-0">
          <v-menu ref="dateMenu" v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="formattedDate"
                class="datepicker-input surgeon-journal-expanded__flat-input"
                append-icon="mdi-calendar"
                @click:append="dateMenu = true"
                label="Dato"
                readonly
                hide-details
                v-on="on"
              />
            </template>
            <v-date-picker v-model="date" no-title @change="dateMenu = false; onDateChange()" color="primary lighten-0"/>
          </v-menu>
        </v-col>
        <v-col cols="5" sm="2" md="2" class="py-0">
          <v-select
            ref="hourSelect"
            v-model="selectedHour"
            :items="hourOptions"
            label="Timer"
            :disabled="!date"
            @change="onHourChange"
            hide-details
            class="surgeon-journal-expanded__flat-input"
            :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
          />
        </v-col>
        <v-col cols="auto" class="py-0 px-0 d-flex align-center" style="height: 56px">
          <span class="text-h6 grey--text">:</span>
        </v-col>
        <v-col cols="5" sm="2" md="2" class="py-0">
          <v-select
            ref="minuteSelect"
            v-model="selectedMinute"
            :items="minuteOptions"
            label="Minutter"
            :disabled="!date"
            @change="onMinutesChange"
            hide-details
            class="surgeon-journal-expanded__flat-input"
            :menu-props="{ offsetY: true, bottom: true, maxHeight: 200 }"
          />
        </v-col>
        <v-col v-if="date" cols="auto" class="py-0 px-0 d-flex align-center" style="height: 56px">
          <v-btn icon @click="resetTime">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4" md="2" class="py-0">
          <v-text-field
            ref="durationField"
            label="Tidsplan"
            append-icon="mdi-timer-outline"
            v-model="operationDuration"
            hide-details
            class="surgeon-journal-expanded__flat-input"
          />
        </v-col>
      </v-row>

      <v-row class="my-0 align-start surgeon-journal-expanded__meta-fields mt-2">
        <v-col cols="12" lg="6" class="py-0">
          <v-select
            :items="treatmentTypes"
            item-text="name"
            item-value="id"
            label="Vælg behandlingstype"
            v-model="treatmentTypeId"
            hide-details
            class="surgeon-journal-expanded__flat-input"
          >
            <template v-slot:item="{ item, on }">
              <v-list-item v-on="on" v-show="item.id !== -100">
                <span>{{ item.name }}</span>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" lg="6" class="py-0">
          <v-select
            :items="surgeons"
            item-text="fullName"
            item-value="id"
            label="Behandler"
            loader-height="1"
            v-model="surgeonId"
            hide-details
            class="surgeon-journal-expanded__flat-input"
          />
        </v-col>
      </v-row>

      <div class="white rounded-xl pa-4 mt-4 mb-4">
        <v-textarea
          v-model="surgeonComment"
          label="Bemærk"
          auto-grow
          :rows="3"
          outlined
          hide-details
        />
        <div class="d-flex justify-start mt-3">
          <v-btn color="primary" depressed :loading="saveLoading" @click="saveVisitInfo">
            Opdater
          </v-btn>
        </div>
      </div>

      <div class="white rounded-xl pa-4 mb-4">
        <div class="d-flex align-center justify-space-between mb-2 surgeon-journal-expanded__action-header">
          <h3 class="text-subtitle-1 text-uppercase mb-0">Oversigt over kirurgens handlinger</h3>
          <v-tooltip v-if="speechRecognitionSupported" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                :color="isListening ? 'error' : 'primary'"
                :disabled="speechInputDisabled"
                @click="toggleSpeechInput"
              >
                <v-icon>{{ isListening ? 'mdi-microphone' : 'mdi-microphone-outline' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ isListening ? 'Stop stemmeindtastning' : 'Stemmeindtastning' }}</span>
          </v-tooltip>
        </div>
        <v-textarea
          v-model="surgeonActionSummary"
          placeholder="Beskriv kirurgens handlinger under besøget..."
          auto-grow
          :rows="3"
          outlined
          hide-details
          :disabled="speechInputDisabled"
          class="mb-4 surgeon-journal-expanded__action-summary"
        />

        <div v-if="hasJournalError" class="d-flex align-start mb-3">
          <v-icon size="23" class="mr-2 mt-1 red--text">mdi-auto-fix</v-icon>
          <span class="text-body-2 red--text">{{ latestJournal.error }}</span>
        </div>
        <div v-if="journalGenerationInProgress" class="d-flex align-center mb-3">
          <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"/>
          <span class="text-body-2 primary--text">Journal genereres – vent venligst...</span>
        </div>
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="text-body-2 grey--text text--darken-1">Journal-kladde</span>
          <div class="d-flex align-center">
            <v-btn
              v-if="showDiscardButton"
              text
              small
              class="px-2 mr-1"
              :disabled="!canDiscardJournalEdits"
              @click="discardJournalEdits"
            >
              Kassere
            </v-btn>
            <v-btn text small class="px-2" @click="copyJournalSuggestion">
              <v-icon v-if="copied" small color="success" class="mr-1">mdi-check</v-icon>
              <v-icon v-else small class="mr-1">mdi-content-copy</v-icon>
              {{ copied ? 'Kopieret!' : 'Kopier' }}
            </v-btn>
          </div>
        </div>
        <v-textarea
          v-model="journalSuggestion"
          class="surgeon-journal-expanded__journal-text"
          auto-grow
          :rows="12"
          outlined
          hide-details
          :placeholder="journalPlaceholder"
        />
        <div class="d-flex justify-end mt-1">
          <v-btn text small class="px-2 grey--text" @click="showLog = !showLog">
            <v-icon small class="mr-1">mdi-history</v-icon>
            Log
          </v-btn>
        </div>

        <v-expand-transition>
          <div v-if="showLog" class="surgeon-journal-expanded__log mt-3">
            <div class="surgeon-journal-expanded__log-header d-flex align-center justify-space-between px-3 py-2">
              <div class="d-flex align-center">
                <v-icon small class="mr-2">mdi-history</v-icon>
                <span class="text-body-2 font-weight-medium">Tidligere genererede journaler</span>
              </div>
              <v-btn icon x-small @click="showLog = false">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-if="sortedJournals.length === 0" class="pa-4 text-body-2 grey--text">
              Ingen journaler endnu.
            </div>
            <div v-else class="surgeon-journal-expanded__log-list">
              <div
                v-for="journal in sortedJournals"
                :key="journal.id"
                class="surgeon-journal-expanded__log-item px-3"
              >
                <div
                  class="surgeon-journal-expanded__log-toggle d-flex align-center justify-space-between"
                  role="button"
                  tabindex="0"
                  @click="toggleLogItem(journal.id)"
                  @keydown.enter.prevent="toggleLogItem(journal.id)"
                >
                  <div class="d-flex align-center text-left">
                    <v-icon small class="mr-2 grey--text">mdi-file-document-outline</v-icon>
                    <span class="text-body-2 font-weight-medium">{{ formatLogDate(journal.createDate) }}</span>
                    <span class="text-caption grey--text ml-2">— {{ journal.surgeonName }}</span>
                  </div>
                  <v-icon small :class="{'rotate-180': expandedLogId === journal.id}">mdi-chevron-down</v-icon>
                </div>
                <v-expand-transition>
                  <div v-if="expandedLogId === journal.id" class="surgeon-journal-expanded__log-body pb-3">
                    <div class="d-flex justify-end mb-1">
                      <v-btn text x-small class="px-2 surgeon-journal-expanded__log-copy-btn" @click.stop="copyLogJournalText(journal)">
                        <v-icon v-if="copiedLogJournalId === journal.id" small color="success" class="mr-1">mdi-check</v-icon>
                        <v-icon v-else small class="mr-1">mdi-content-copy</v-icon>
                        {{ copiedLogJournalId === journal.id ? 'Kopieret!' : 'Kopier' }}
                      </v-btn>
                    </div>
                    <pre class="surgeon-journal-expanded__log-text pa-3 ma-0">{{ journal.journalText || journal.aiGeneratedJournalText }}</pre>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </div>
        </v-expand-transition>

        <div class="d-flex align-center mt-4 mb-2">
          <v-icon size="23" class="mr-2">mdi-auto-fix</v-icon>
          <span class="text-body-2 font-italic grey--text text--darken-1">Kommentar til ai til fremtidig journalisering</span>
        </div>
        <v-textarea
          v-model="aiComment"
          placeholder="Kommentar til AI"
          auto-grow
          :rows="4"
          outlined
          hide-details
        />
      </div>

      <div class="d-flex flex-wrap align-center surgeon-journal-expanded__actions mb-6">
        <v-btn
          color="primary"
          depressed
          class="mr-4"
          :loading="generateLoading"
          :disabled="generateDisabled"
          @click="generateJournal"
        >
          Generer Journal-kladde
        </v-btn>
        <v-btn
          color="primary"
          depressed
          class="mr-4"
          :loading="approveLoading"
          :disabled="approveDisabled"
          @click="approveJournal"
        >
          Godkend
        </v-btn>
        <v-checkbox
          v-model="trainAiOnChanges"
          :disabled="approveDisabled || !canTrainAiOnChanges"
          hide-details
          class="mt-0 pt-0"
        >
          <template v-slot:label>
            <span class="text-body-2">Brug ændringer til AI-træning</span>
          </template>
        </v-checkbox>
      </div>

      <h2 class="text-subtitle-1 text-uppercase mb-2">Opsummering af henvisning</h2>
      <div class="white rounded-xl mb-6 pa-5">
        <table class="info-table">
          <tr>
            <td>Klinik:</td>
            <td>{{ visitData.clinicName }}</td>
          </tr>
          <tr>
            <td>Henvisernavn:</td>
            <td>{{ visitData.doctorFullName }}</td>
          </tr>
          <tr>
            <td>Henvisningstype:</td>
            <td>{{ visitData.referenceType }}</td>
          </tr>
          <tr v-if="visitData.patientAge != null">
            <td>Patientens alder:</td>
            <td>{{ visitData.patientAge }}</td>
          </tr>
          <tr>
            <td>Indkaldelse:</td>
            <td>{{ visitData.callType }}</td>
          </tr>
          <tr>
            <td>Betaling:</td>
            <td>{{ visitData.paymentType }}</td>
          </tr>
          <tr>
            <td>Diagnose:</td>
            <td>{{ visitData.diagnose }}</td>
          </tr>
          <tr>
            <td>Tidligere behandling:</td>
            <td>{{ visitData.previousTreatment }}</td>
          </tr>
          <tr>
            <td>Ønsket behandling:</td>
            <td>{{ visitData.desiredTreatment }}</td>
          </tr>
          <tr>
            <td>Evt. kommentar:</td>
            <td>{{ visitData.comment }}</td>
          </tr>
          <tr>
            <td>Sygesikring af DANMARK:</td>
            <td>{{ visitData.healthInsuranceType }}</td>
          </tr>
          <tr v-if="visitData.price">
            <td>Pris:</td>
            <td>{{ visitData.price }}</td>
          </tr>
        </table>
      </div>

      <h2 class="text-subtitle-1 text-uppercase mb-2">Behandlingsplan</h2>
      <div class="white rounded-xl mb-4 pa-5">
        <div v-if="!visitData.visitAppointments || visitData.visitAppointments.length === 0" class="text-body-2 grey--text">
          Ingen aftaler i behandlingsplanen.
        </div>
        <div v-else class="visit-appointments-table surgeon-journal-expanded__appointments-table" @click.stop>
          <table class="surgeon-journal-expanded__appointments-table-inner">
            <thead>
              <tr>
                <th class="text-left">Dato</th>
                <th class="text-left">Behandlingstype</th>
                <th class="text-left">Ekstern kode</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(appointment, index) in visitData.visitAppointments" :key="index">
                <td>{{ formatAppointmentDate(appointment.appointmentDate) }}</td>
                <td>{{ appointment.treatment }}</td>
                <td>{{ appointment.externalAppointmentCode }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
  import api from '@/api/api';
  import methods from '@/utils/methods';

  export default {
    name: 'SurgeonJournalExpanded',

    props: {
      visitItem: {
        type: Object,
        required: true,
      },
    },

    data: function () {
      return {
        getLoading: true,
        saveLoading: false,
        generateLoading: false,
        approveLoading: false,
        visitData: null,
        treatmentTypeId: null,
        surgeonId: null,
        surgeonComment: '',
        surgeonActionSummary: '',
        journalSuggestion: '',
        aiGeneratedJournalText: '',
        aiComment: '',
        editableSurgeonJournalId: null,
        showLog: false,
        expandedLogId: null,
        copied: false,
        copiedLogJournalId: null,
        journalGenerationInProgress: false,
        journalPollTimeoutId: null,
        speechRecognitionSupported: false,
        isListening: false,
        recognition: null,
        dateMenu: false,
        date: null,
        selectedHour: null,
        selectedMinute: null,
        operationDuration: '',
        trainAiOnChanges: false,
      };
    },

    computed: {
      treatmentTypes: function () {
        return this.$store.state.treatmentTypes;
      },
      surgeons: function () {
        return this.$store.state.surgeons;
      },
      formattedDate: function () {
        if (this.date) {
          return methods.formatDate(this.date);
        }
        return '';
      },
      hourOptions: function () {
        const standardHours = Array.from({length: 14}, (_, i) => ({
          text: (7 + i).toString().padStart(2, '0'),
          value: (7 + i).toString().padStart(2, '0'),
        }));

        if (this.selectedHour && !standardHours.find((h) => h.value === this.selectedHour)) {
          standardHours.push({
            text: this.selectedHour,
            value: this.selectedHour,
          });
          standardHours.sort((a, b) => parseInt(a.value) - parseInt(b.value));
        }

        return standardHours;
      },
      minuteOptions: function () {
        const standardMinutes = Array.from({length: 12}, (_, i) => ({
          text: (i * 5).toString().padStart(2, '0'),
          value: (i * 5).toString().padStart(2, '0'),
        }));

        if (this.selectedMinute && !standardMinutes.find((m) => m.value === this.selectedMinute)) {
          standardMinutes.push({
            text: this.selectedMinute,
            value: this.selectedMinute,
          });
          standardMinutes.sort((a, b) => parseInt(a.value) - parseInt(b.value));
        }

        return standardMinutes;
      },
      sortedJournals: function () {
        if (!this.visitData?.surgeonJournals) {
          return [];
        }
        return [...this.visitData.surgeonJournals].sort((a, b) => new Date(b.createDate) - new Date(a.createDate));
      },
      latestJournal: function () {
        return this.sortedJournals[0] || null;
      },
      hasJournalError: function () {
        return !!(this.latestJournal && !this.latestJournal.isApproved && this.latestJournal.error?.trim());
      },
      journalPlaceholder: function () {
        return this.editableSurgeonJournalId ? '' : 'Journal-kladde vises her...';
      },
      showDiscardButton: function () {
        return !!this.editableSurgeonJournalId && !!this.aiGeneratedJournalText?.trim();
      },
      canDiscardJournalEdits: function () {
        return this.journalSuggestion !== this.aiGeneratedJournalText;
      },
      generateDisabled: function () {
        return this.generateLoading || this.journalGenerationInProgress || this.editableSurgeonJournalId != null || !this.surgeonActionSummary?.trim();
      },
      approveDisabled: function () {
        return this.approveLoading || this.journalGenerationInProgress || !this.editableSurgeonJournalId || !this.journalSuggestion?.trim();
      },
      canTrainAiOnChanges: function () {
        return this.journalSuggestion !== this.aiGeneratedJournalText || !!this.aiComment?.trim();
      },
      speechInputDisabled: function () {
        return this.getLoading || this.generateLoading || this.journalGenerationInProgress;
      },
    },

    watch: {
      canTrainAiOnChanges: function (canTrain) {
        if (!canTrain) {
          this.trainAiOnChanges = false;
        }
      },
    },

    mounted: function () {
      this.initSpeechRecognition();
      this.loadVisitData();
    },

    destroyed: function () {
      this.stopJournalPolling();
      this.stopSpeechInput();
    },

    methods: {
      initSpeechRecognition: function () {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          return;
        }
        this.speechRecognitionSupported = true;
        this.recognition = new SpeechRecognition();
        this.recognition.continuous = true;
        this.recognition.interimResults = false;
        this.recognition.lang = 'da-DK';

        this.recognition.onresult = (event) => {
          let transcript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              transcript += event.results[i][0].transcript;
            }
          }
          if (transcript.trim()) {
            const current = this.surgeonActionSummary?.trim();
            this.surgeonActionSummary = current ? `${current} ${transcript.trim()}` : transcript.trim();
          }
        };

        this.recognition.onerror = (event) => {
          if (event.error !== 'aborted') {
            methods.showGlobalSnackbar('error', 'Stemmeindtastning fejlede.');
          }
          this.isListening = false;
        };

        this.recognition.onend = () => {
          if (this.isListening) {
            try {
              this.recognition.start();
            } catch (e) {
              this.isListening = false;
            }
          }
        };
      },
      toggleSpeechInput: function () {
        if (!this.recognition) {
          return;
        }
        if (this.isListening) {
          this.stopSpeechInput();
          return;
        }
        try {
          this.recognition.start();
          this.isListening = true;
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Kunne ikke starte stemmeindtastning.');
        }
      },
      stopSpeechInput: function () {
        this.isListening = false;
        if (this.recognition) {
          try {
            this.recognition.stop();
          } catch (e) {}
        }
      },
      isJournalGenerationPending: function (journal) {
        if (!journal || journal.isApproved) {
          return false;
        }
        const hasAiText = !!journal.aiGeneratedJournalText?.trim();
        const hasError = !!journal.error?.trim();
        return !hasAiText && !hasError;
      },
      stopJournalPolling: function () {
        if (this.journalPollTimeoutId) {
          clearTimeout(this.journalPollTimeoutId);
          this.journalPollTimeoutId = null;
        }
        this.journalGenerationInProgress = false;
      },
      fetchSurgeonJournalsData: async function (silent) {
        const {data} = await api.getSurgeonJournals(this.visitItem.id);
        this.visitData = data;
        this.applyVisitDataToForm();
        return data;
      },
      pollForJournalGeneration: function () {
        this.stopJournalPolling();
        this.journalGenerationInProgress = true;

        const poll = async () => {
          try {
            await this.fetchSurgeonJournalsData(true);
            if (!this.isJournalGenerationPending(this.latestJournal)) {
              this.stopJournalPolling();
              return;
            }
            this.journalPollTimeoutId = setTimeout(poll, 5000);
          } catch (e) {
            this.stopJournalPolling();
            methods.showGlobalSnackbar('error', 'Kunne ikke hente journaldata. Prøv igen senere.');
          }
        };

        this.journalPollTimeoutId = setTimeout(poll, 5000);
      },
      resumeJournalPollingIfNeeded: function () {
        if (this.isJournalGenerationPending(this.latestJournal)) {
          this.pollForJournalGeneration();
        }
      },
      loadVisitData: async function () {
        try {
          this.getLoading = true;
          await this.fetchSurgeonJournalsData(true);
          this.resumeJournalPollingIfNeeded();
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Kunne ikke hente journaldata. Prøv igen senere.');
        } finally {
          this.getLoading = false;
        }
      },
      applyVisitDataToForm: function () {
        this.treatmentTypeId = this.visitData.treatmentTypeId;
        this.surgeonId = this.visitData.surgeonId;
        this.surgeonComment = this.visitData.surgeonComment || '';
        this.setOperationFieldsFromVisitData();
        this.setJournalFormFromData();
      },
      setOperationFieldsFromVisitData: function () {
        if (this.visitData.operationDate) {
          this.date = this.getDatepickerDate(this.visitData.operationDate);
          const time = methods.getTimeFromIso(this.visitData.operationDate);
          const [hours, minutes] = time.split(':');
          this.selectedHour = hours;
          this.selectedMinute = minutes;
        } else {
          this.date = null;
          this.selectedHour = null;
          this.selectedMinute = null;
        }
        this.operationDuration = this.visitData.operationDuration || '';
      },
      getDatepickerDate: function (date) {
        const dateArray = date.split('-');
        const simpleYear = dateArray[0];
        const simpleMonth = dateArray[1];
        const simpleDay = dateArray[2].split('T')[0];
        return `${simpleYear}-${simpleMonth}-${simpleDay}`;
      },
      onDateChange: function () {
        this.$nextTick(() => {
          this.$refs.hourSelect.focus();
          this.$refs.hourSelect.activateMenu();
        });
      },
      onHourChange: function () {
        this.updateTime();
        this.$nextTick(() => {
          this.$refs.minuteSelect.focus();
          this.$refs.minuteSelect.activateMenu();
        });
      },
      onMinutesChange: function () {
        this.updateTime();
        this.$nextTick(() => {
          this.$refs.durationField.focus();
        });
      },
      updateTime: function () {
        if (this.selectedHour === null) {
          this.selectedHour = '00';
        }
        if (this.selectedMinute === null) {
          this.selectedMinute = '00';
        }
      },
      resetTime: function () {
        this.date = null;
        this.selectedHour = null;
        this.selectedMinute = null;
      },
      getJournalDisplayText: function (journal) {
        if (journal.journalText?.trim()) {
          return journal.journalText;
        }
        return journal.aiGeneratedJournalText || '';
      },
      resetJournalForm: function () {
        this.surgeonActionSummary = '';
        this.journalSuggestion = '';
        this.aiGeneratedJournalText = '';
        this.aiComment = '';
        this.editableSurgeonJournalId = null;
      },
      fillJournalFormFromJournal: function (journal) {
        this.surgeonActionSummary = journal.surgeonActionSummary || '';
        this.aiComment = journal.aiComment || '';
        this.editableSurgeonJournalId = journal.id;
        this.aiGeneratedJournalText = journal.aiGeneratedJournalText || '';
        this.journalSuggestion = this.getJournalDisplayText(journal);
      },
      setJournalFormFromData: function () {
        const latest = this.latestJournal;
        if (!latest || latest.isApproved) {
          this.resetJournalForm();
        } else {
          this.fillJournalFormFromJournal(latest);
        }
      },
      discardJournalEdits: function () {
        this.journalSuggestion = this.aiGeneratedJournalText;
      },
      formatLogDate: function (date) {
        if (!date) {
          return '';
        }
        return `${methods.getDateFromIso(date)} ${methods.getTimeFromIso(date)}`;
      },
      formatAppointmentDate: function (date) {
        if (!date) {
          return '';
        }
        return `${methods.getSimpleDate(date)} ${methods.getSimpleTime(date)}`;
      },
      toggleLogItem: function (journalId) {
        this.expandedLogId = this.expandedLogId === journalId ? null : journalId;
      },
      copyJournalSuggestion: async function () {
        const text = this.journalSuggestion || '';
        try {
          await navigator.clipboard.writeText(text);
          this.copied = true;
          setTimeout(() => {
            this.copied = false;
          }, 2000);
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Kunne ikke kopiere teksten.');
        }
      },
      copyLogJournalText: async function (journal) {
        const text = journal?.journalText || journal?.aiGeneratedJournalText || '';
        if (!text.trim()) {
          methods.showGlobalSnackbar('error', 'Ingen tekst at kopiere.');
          return;
        }
        try {
          await navigator.clipboard.writeText(text);
          this.copiedLogJournalId = journal.id;
          setTimeout(() => {
            if (this.copiedLogJournalId === journal.id) {
              this.copiedLogJournalId = null;
            }
          }, 2000);
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Kunne ikke kopiere teksten.');
        }
      },
      saveQueryData: function () {
        let queryData = [];
        if (this.treatmentTypeId >= 0) {
          queryData.push({op: 'replace', path: '/treatmentType', value: this.treatmentTypeId});
        }
        if (this.surgeonId >= 0) {
          queryData.push({op: 'replace', path: '/surgeonId', value: this.surgeonId});
        }
        queryData.push({op: 'replace', path: '/surgeonComment', value: this.surgeonComment});
        queryData.push({op: 'replace', path: '/operationDate', value: this.date ? new Date(this.date).toISOString() : null});
        queryData.push({op: 'replace', path: '/operationTimeHours', value: this.selectedHour ? parseInt(this.selectedHour) : null});
        queryData.push({op: 'replace', path: '/operationTimeMinutes', value: this.selectedMinute ? parseInt(this.selectedMinute) : null});
        queryData.push({op: 'replace', path: '/operationDuration', value: this.operationDuration || ''});
        return queryData;
      },
      saveVisitInfo: async function () {
        try {
          this.saveLoading = true;
          await api.updateVisitPatch(this.saveQueryData(), this.visitItem.id, null);
          let newVisit = await api.getVisitInfo(this.visitItem.id);
          const {data} = await api.getSurgeonJournals(this.visitItem.id);
          this.visitData = data;
          this.applyVisitDataToForm();
          this.$emit('updateVisitItem', newVisit.data);
          this.$emit('updateCommonVisitInfo', newVisit.data);
          methods.showGlobalSnackbar('success', 'Oplysninger opdateret.');
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Server error. Noget gik galt. Prøv igen senere.');
        } finally {
          this.saveLoading = false;
        }
      },
      generateJournal: async function () {
        if (!this.surgeonActionSummary?.trim()) {
          methods.showGlobalSnackbar('error', 'Udfyld handling før generering af journal.');
          return;
        }
        try {
          this.generateLoading = true;
          this.stopSpeechInput();
          this.stopJournalPolling();
          await api.createSurgeonJournal(this.visitItem.id, {
            SurgeonActionSummary: this.surgeonActionSummary,
          });
          await this.fetchSurgeonJournalsData(true);
          this.resumeJournalPollingIfNeeded();
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Kunne ikke generere journal. Prøv igen senere.');
        } finally {
          this.generateLoading = false;
        }
      },
      approveJournal: async function () {
        if (!this.editableSurgeonJournalId) {
          return;
        }
        try {
          this.approveLoading = true;
          await api.approveSurgeonJournal(this.editableSurgeonJournalId, {
            ApprovedJournalText: this.journalSuggestion,
            AiComment: this.aiComment || '',
            TrainAiOnChanges: this.trainAiOnChanges,
          });
          await this.loadVisitData();
          const newVisit = await api.getVisitInfo(this.visitItem.id);
          this.$emit('updateVisitItem', newVisit.data);
          this.$emit('updateCommonVisitInfo', newVisit.data);
          methods.showGlobalSnackbar('success', 'Journal godkendt. Din journal-kopi er nu aktiveret under LOG-filer.');
        } catch (e) {
          methods.showGlobalSnackbar('error', 'Kunne ikke godkende journal. Prøv igen senere.');
        } finally {
          this.approveLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .surgeon-journal-expanded__content {
    max-width: 1140px;
  }

  .surgeon-journal-expanded__action-header {
    gap: 12px;
  }

  .surgeon-journal-expanded__action-summary {
    font-size: 13px;
  }

  .surgeon-journal-expanded__journal-text {
    font-family: monospace;
    font-size: 13px;
    line-height: 1.7;
  }

  .surgeon-journal-expanded__log {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background: #f8fafc;
  }

  .surgeon-journal-expanded__log-header {
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
  }

  .surgeon-journal-expanded__log-item {
    border-bottom: 1px solid #eeeeee;
    padding-top: 4px;
    padding-bottom: 4px;

    &:last-child {
      border-bottom: 0;
    }
  }

  .surgeon-journal-expanded__log-body {
    padding: 0 4px;
  }

  .surgeon-journal-expanded__log-copy-btn {
    min-width: auto;
    color: #607d8b !important;
  }

  .surgeon-journal-expanded__log-toggle {
    width: 100%;
    min-height: 28px;
    cursor: pointer;
    outline: none;

    &:hover,
    &:active,
    &:focus {
      background: transparent;
    }
  }

  .surgeon-journal-expanded__log-text {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    font-family: monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .surgeon-journal-expanded__flat-input ::v-deep .v-input__slot {
    background: transparent !important;
  }

  .surgeon-journal-expanded__flat-input ::v-deep .v-input--is-focused .v-input__slot,
  .surgeon-journal-expanded__flat-input ::v-deep .v-input:hover .v-input__slot,
  .surgeon-journal-expanded__flat-input ::v-deep .v-input--is-disabled .v-input__slot {
    background: transparent !important;
  }

  .surgeon-journal-expanded__actions {
    gap: 0;
  }

  .visit-appointments-table {
    line-height: 1.2;
    border-radius: 4px;
    background: none;
    border: thin solid #d3d5d7;
    color: black;
    max-width: 480px;
    pointer-events: auto;

    table {
      width: 100%;
      border-spacing: 0;
      table-layout: fixed;
      pointer-events: none;
    }

    th,
    td {
      pointer-events: none;
    }

    th {
      border-bottom: thin solid #d3d5d7;
      padding: 4px 16px;
      height: 32px;
      font-weight: 600;
      font-size: 13px;
      background: transparent !important;
    }

    td {
      padding: 4px 16px;
      height: 32px;
      font-size: 13px;
      background: transparent !important;
    }

    tr:not(:last-child) > td {
      border-bottom: thin solid #d3d5d7;
    }

    tbody tr {
      background: transparent !important;
      background-color: transparent !important;
      cursor: default;
      box-shadow: none !important;
      transition: none !important;
    }

    tbody tr:hover,
    tbody tr:active,
    tbody tr:focus,
    tbody tr:hover td,
    tbody tr:active td,
    tbody tr:focus td,
    tbody tr:hover th,
    tbody tr:active th,
    tbody tr:focus th {
      background: transparent !important;
      background-color: transparent !important;
      box-shadow: none !important;
      cursor: default;
    }
  }

  .rotate-180 {
    transform: rotate(180deg);
  }

  .info-table {
    border-collapse: collapse;
    width: 100%;

    tr {
      &:nth-child(2n) {
        background: #f5f5f5;
      }
    }

    td {
      padding: 5px 10px;
      vertical-align: top;

      &:first-child {
        width: 200px;
        font-weight: 500;
      }
    }
  }
</style>
